import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

const goals = [
  {
    key: 'ticketing',
    title: 'Configure Ticketing system',
    desc: "Turn support emails into tickets and keep track of your customers’ requests. Optimize your support workflow",
    icon: '📩',
  },
  {
    key: 'leads',
    title: 'Generate leads',
    desc: 'Capture quality leads with Flows - automated messages that engage your audience at the right time',
    icon: '📣',
  },
  {
    key: 'sales',
    title: 'Increase sales',
    desc: 'Boost your sales with Flows - automated conversion paths that trigger at specific moments',
    icon: '💸',
  },
  {
    key: 'support',
    title: 'Offer live support',
    desc: 'Drive customer satisfaction with live chat, video calls, and multichannel communication',
    icon: '💬',
  },
  {
    key: 'ai',
    title: 'Automate my support system with AI Agent',
    desc: "Win up to 70% of your team’s time back. Answer customer questions and automate repetitive tasks",
    icon: '🤖',
  },
];

const channelsAll = [
  { key: 'webchat', label: 'Website (via live chat)' },
  { key: 'email', label: 'Email' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'facebook', label: 'Facebook Messenger' },
  { key: 'other', label: 'Other' },
];

export default function OnboardingGoals() {
  const navigate = useNavigate();
  const [selectedGoal, setSelectedGoal] = useState('');
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleChannel = (key) => {
    setChannels((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      try {
        localStorage.setItem('onboarding_goal', selectedGoal);
        localStorage.setItem('onboarding_channels', JSON.stringify(channels));
      } catch (_) {}

      // Persist to DB (idempotent upsert)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          await supabase
            .from('user_onboarding')
            .upsert({
              user_id: userId,
              goal: selectedGoal || null,
              channels: Array.isArray(channels) ? channels : null,
            }, { onConflict: 'user_id' });
        }
      } catch (e) {
        // Non-blocking
        console.warn('[OnboardingGoals] Failed to persist onboarding answers:', e);
      }
    } finally {
      setSaving(false);
      navigate('/chat');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Stack spacing={4}>
            <div>
              <Typography variant="h6" gutterBottom>My main goal for using ComturkAI is…</Typography>
              <Grid container spacing={2}>
                {goals.map((g) => (
                  <Grid item xs={12} sm={6} key={g.key}>
                    <Paper
                      variant="outlined"
                      onClick={() => setSelectedGoal(g.key)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: selectedGoal === g.key ? 'primary.main' : 'divider',
                        boxShadow: selectedGoal === g.key ? 2 : 0,
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Typography variant="subtitle1">{g.icon} {g.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{g.desc}</Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </div>

            <div>
              <Typography variant="h6" gutterBottom>How would you like to interact with your customers?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Select all that apply</Typography>
              <Grid container spacing={2}>
                {channelsAll.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.key}>
                    <Paper
                      variant="outlined"
                      sx={{ 
                        p: 1, 
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: channels.includes(c.key) ? 'primary.main' : 'divider',
                        bgcolor: channels.includes(c.key) ? 'action.selected' : 'background.paper',
                        '&:hover': {
                          bgcolor: channels.includes(c.key) ? 'action.selected' : 'action.hover',
                        }
                      }}
                      onClick={() => toggleChannel(c.key)}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={channels.includes(c.key)} />}
                        label={c.label}
                        sx={{ 
                          width: '100%', 
                          m: 0,
                          pointerEvents: 'none',
                          userSelect: 'none'
                        }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </div>

            <Divider />

            <Stack direction="row" spacing={2}>
              <Button onClick={() => navigate('/onboarding/profile-3')}>Go back</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" onClick={handleContinue} disabled={saving}>Let’s go!</Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1200&auto=format&fit=crop"
                alt="Live chat preview"
                sx={{ width: '100%', borderRadius: 2, display: 'block' }}
              />
              <Box sx={{ position: 'absolute', left: 24, top: 220, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, px: 1.5, py: 0.75, boxShadow: 3 }}>
                <Typography variant="caption">Hello, what are your opening times?</Typography>
              </Box>
              <Box sx={{ position: 'absolute', left: 160, top: 260, bgcolor: 'background.paper', borderRadius: 2, px: 1.5, py: 0.75, boxShadow: 2 }}>
                <Typography variant="caption">Hi there! 😊</Typography>
              </Box>
              <Box sx={{ position: 'absolute', left: 120, top: 300, bgcolor: 'background.paper', borderRadius: 2, px: 1.5, py: 0.75, boxShadow: 2 }}>
                <Typography variant="caption">We are open Monday–Saturday, 10am–6pm</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}





